/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Suspense, lazy, useMemo } from 'react';

import type { SourcesByResolution } from '@coursera-web/video-player';

import Imgix from 'js/components/Imgix';

import { Skeleton } from '@coursera/cds-core';

import type { MediaAsset } from 'bundles/premium-hub/components/degrees/redesign/DomainIntroModule';

// VideoPlayer pulls in the video.js runtime (~140kB before gzip). Defer it so it
// stays out of the premium-hub bundle until this module actually renders a video.
const VideoPlayer = lazy(() => import('bundles/premium-hub/components/shared/VideoPlayer'));

export type DomainIntroMediaProps = {
  heading: string;
  media?: MediaAsset;
};

const styles = {
  root: css`
    aspect-ratio: 16 / 9;
    overflow: hidden;
    border-radius: var(--cds-border-radius-100);
  `,
  image: css`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  `,
  player: css`
    --video-player-border-radius: var(--cds-border-radius-100);

    border: none;
  `,
};

const DomainIntroMedia = ({ heading, media }: DomainIntroMediaProps) => {
  const isVideo = media?.contentType?.startsWith('video/') ?? false;

  const sources = useMemo<SourcesByResolution | undefined>(
    () => (isVideo && media?.url ? { '720p': { [media.contentType as string]: media.url } } : undefined),
    [isVideo, media?.contentType, media?.url]
  );

  if (isVideo && sources) {
    return (
      <div css={styles.root} data-testid="domain-intro-media-video">
        <Suspense fallback={<Skeleton variant="rect" height="100%" width="100%" />}>
          <VideoPlayer
            css={styles.player}
            sources={sources}
            layoutMode="fill"
            crossOrigin="anonymous"
            disableGlobalHotkeys
            name={heading}
            ariaLabel={media?.description ?? undefined}
            sentryErrorSource="DomainIntroMedia"
          />
        </Suspense>
      </div>
    );
  }

  if (media?.url) {
    return (
      <div css={styles.root} data-testid="domain-intro-media-image">
        <Imgix
          src={media.url}
          alt={media.description ?? ''}
          css={styles.image}
          loading="lazy"
          imgParams={{ auto: 'format,compress', fit: 'fill' }}
        />
      </div>
    );
  }

  return null;
};

export default DomainIntroMedia;
